import { Injectable } from '@angular/core';
import { LockPointEnum } from '../../enums/lockpoint';
import { BoundingBox } from '../../models/bounding-box';
import { CropBox } from '../../models/crop-box';
import { EditPhoto } from '../../models/edit-photo.viewmodel';
import { BoundingBoxService } from '../bounding-box/bounding-box.service';

const MIN_PHOTO_DIMENSION = 500; // Max size of photo must be minimum of 500px
const MIN_PHOTO_WIDTH = 10; // px
const MIN_PHOTO_HEIGHT = 10; // px

@Injectable({
    providedIn: 'root'
})
export class PhotoTransformService {
    constructor(private _boundingBoxService: BoundingBoxService) {}

    public hasCropBox(selectedPhoto: EditPhoto): boolean {
        if (
            !selectedPhoto.transformedPhoto ||
            !selectedPhoto.transformedPhoto.crop
        ) {
            return false;
        }

        if (
            !selectedPhoto.transformedPhoto.crop.min ||
            !selectedPhoto.transformedPhoto.crop.max
        ) {
            return false;
        }

        if (
            selectedPhoto.transformedPhoto.crop.min.x > 0 ||
            selectedPhoto.transformedPhoto.crop.min.y > 0 ||
            selectedPhoto.transformedPhoto.crop.max.x > 0 ||
            selectedPhoto.transformedPhoto.crop.max.y > 0
        ) {
            return true;
        }

        return false;
    }

    public isImageTooSmall(selectedPhoto: EditPhoto): boolean {
        const { imageWidth, imageHeight } = selectedPhoto;

        if (Math.max(imageWidth, imageHeight) < MIN_PHOTO_DIMENSION) {
            return true;
        }

        return false;
    }

    public isCropBoxValid(selectedPhoto: EditPhoto): boolean {
        if (!this.hasCropBox(selectedPhoto)) {
            return true;
        }

        const { x, y, width, height } = selectedPhoto.cropBox;
        const roundedWidth = Math.ceil(width);
        const roundedHeight = Math.ceil(height);

        if (Math.floor(x) < 0 || Math.floor(y) < 0) {
            return false;
        }

        if (
            roundedWidth < MIN_PHOTO_WIDTH ||
            roundedHeight < MIN_PHOTO_HEIGHT
        ) {
            return false;
        }

        if (
            roundedWidth < MIN_PHOTO_DIMENSION ||
            roundedHeight < MIN_PHOTO_DIMENSION
        ) {
            return false;
        }

        return true;
    }

    public toggleCropBox(selectedPhoto: EditPhoto): EditPhoto {
        if (selectedPhoto.cropBox != null) {
            const editedPhoto = {
                ...selectedPhoto,
                transformedPhoto: {
                    ...selectedPhoto.transformedPhoto,
                    crop: null as BoundingBox
                },
                cropBox: null as CropBox
            };

            return editedPhoto;
        } else {
            if (
                !this.hasCropBox(selectedPhoto) ||
                this._isDefaultCrop(selectedPhoto.cropBox)
            ) {
                const maxBoundingBox = this.getMaxBoundingBox(selectedPhoto);
                return this.setPhotoCropBoundingBox(
                    selectedPhoto,
                    maxBoundingBox
                );
            }
        }

        return selectedPhoto;
    }

    public applyCropBox(
        selectedPhoto: EditPhoto,
        lockPoint: LockPointEnum = LockPointEnum.none
    ): EditPhoto {
        if (!selectedPhoto.cropBox) {
            return selectedPhoto;
        }

        const { x, y, width, height } = selectedPhoto.cropBox;

        const boundingBox: BoundingBox = {
            min: { x: x, y: y },
            max: { x: x + width, y: y + height }
        };

        return this.setPhotoCropBoundingBox(
            selectedPhoto,
            boundingBox,
            lockPoint
        );
    }

    public applyRotation(selectedPhoto: EditPhoto): EditPhoto {
        const rotation = this.getPhotoRotation(selectedPhoto, 90);
        return this._setPhotoRotation(selectedPhoto, rotation);
    }

    // //
    // // LISTING PHOTO TRANSFORMS:
    // //

    public getPhotoRotation(
        selectedPhoto: EditPhoto,
        rotate = 0
    ): number {
        return ((selectedPhoto.rotation || 0) + (rotate ? rotate : 0)) % 360;
    }

    public setPhotoCropBoundingBox(
        selectedPhoto: EditPhoto,
        boundingBox: BoundingBox,
        lockPoint: LockPointEnum = LockPointEnum.none
    ): EditPhoto {
        const correctedBoundingBox = this._getCorrectPhotoCropBoundingBox(
            selectedPhoto,
            boundingBox,
            lockPoint
        );

        let editPhoto = {
            ...selectedPhoto,
            transformedPhoto: {
                ...selectedPhoto?.transformedPhoto,
                crop: correctedBoundingBox
            }
        } as EditPhoto;
        editPhoto = {
            ...editPhoto,
            cropBox: this._getPhotoCropBoxFromCropBoundingBox(editPhoto)
        };

        return editPhoto;
    }

    // //
    // // HELPER METHODS:
    // //

    public getMaxBoundingBox(selectedPhoto: EditPhoto): BoundingBox {
        const [
            imageWidth,
            imageHeight
        ] = this._getSwitchedPhotoDimensionsIfRotated(selectedPhoto);

        const boundingBox: BoundingBox = {
            min: { x: 0, y: 0 },
            max: { x: imageWidth, y: imageHeight }
        };
        return boundingBox;
    }

    private _setPhotoRotation(
        selectedPhoto: EditPhoto,
        rotation: number
    ): EditPhoto {
        const isMaxBoundingBox = this._boundingBoxService.areBoundingBoxesEqual(
            selectedPhoto.transformedPhoto?.crop,
            this.getMaxBoundingBox(selectedPhoto)
        );

        let photoModel = {
            ...selectedPhoto,
            transformedPhoto: {
                ...selectedPhoto.transformedPhoto
            },
            rotation: rotation
        };
        if (photoModel.cropBox) {
            if (
                isMaxBoundingBox &&
                photoModel.imageWidth &&
                photoModel.imageHeight
            ) {
                photoModel = {
                    ...photoModel,
                    transformedPhoto: {
                        ...photoModel.transformedPhoto,
                        crop: this.getMaxBoundingBox(photoModel)
                    }
                };
            } else {
                const correctedBoundingBox = this._getCorrectPhotoCropBoundingBox(
                    photoModel,
                    selectedPhoto.transformedPhoto?.crop
                );

                photoModel = {
                    ...photoModel,
                    transformedPhoto: {
                        ...photoModel.transformedPhoto,
                        crop: correctedBoundingBox
                    }
                };
            }

            photoModel = {
                ...photoModel,
                cropBox: this._getPhotoCropBoxFromCropBoundingBox(photoModel)
            };
        }
        return photoModel;
    }

    private _getPhotoCropBoxFromCropBoundingBox(
        selectedPhoto: EditPhoto
    ): CropBox {
        if (!selectedPhoto.transformedPhoto.crop) {
            return {
                x: 0,
                y: 0,
                width: 0,
                height: 0
            };
        }

        const { min, max } = selectedPhoto.transformedPhoto.crop;

        return {
            x: min.x,
            y: min.y,
            width: max.x - min.x,
            height: max.y - min.y
        };
    }

    private _getCorrectPhotoCropBoundingBox(
        selectedPhoto: EditPhoto,
        boundingBox: BoundingBox,
        lockPoint = -1
    ): BoundingBox {
        const [
            imageWidth,
            imageHeight
        ] = this._getSwitchedPhotoDimensionsIfRotated(selectedPhoto);

        if (
            Math.max(imageWidth, imageHeight) < MIN_PHOTO_DIMENSION ||
            !imageWidth ||
            !imageHeight ||
            this._isDefaultCrop(selectedPhoto.cropBox)
        ) {
            return selectedPhoto.imageWidth && selectedPhoto.imageHeight
                ? this.getMaxBoundingBox(selectedPhoto)
                : boundingBox;
        }

        const outerBounds = [0, 0, imageWidth, imageHeight];
        const restrictedBoundingBox = this._boundingBoxService.restrictBoundingBoxToOuterBounds(
            boundingBox,
            outerBounds,
            lockPoint,
            MIN_PHOTO_WIDTH,
            MIN_PHOTO_HEIGHT,
            MIN_PHOTO_DIMENSION
        );
        return restrictedBoundingBox;
    }

    private _getSwitchedPhotoDimensionsIfRotated(
        photo: EditPhoto
    ): Array<number> {
        let { imageWidth, imageHeight } = photo;

        if (
            photo.rotation &&
            (photo.rotation === 90 || photo.rotation === 270)
        ) {
            imageWidth = photo.imageHeight;
            imageHeight = photo.imageWidth;
        }

        return [imageWidth, imageHeight];
    }

    private _isDefaultCrop(cropBox: CropBox): boolean {
        if (!cropBox) {
            return true;
        }
        const { x, y, width, height } = cropBox;

        return x === 0 && y === 0 && width === 0 && height === 0;
    }
}
